import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  Search, Plus, Filter, X, FileText, Loader2, ChevronDown, ChevronUp, Download, Receipt, Trash2, ClipboardList, Clock,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { ConvertCreditWarning } from '../dealers/ConvertCreditWarning';
import { WonFollowUpPrompt, readFollowUpDue, type FollowUpDue } from '../admin/WonFollowUpPrompt';
import { readCreditWarning, type CreditWarning } from '../dealers/dealerLedgerApi';

interface Proposal {
  id: string;
  number: string;
  title: string;
  status: string;
  validUntil?: string;
  total: number;
  createdAt: string;
  contact?: { id: string; firstName: string; lastName: string } | null;
  account?: { id: string; name: string } | null;
  // WS-C — raw gate column ('PENDING_APPROVAL' | null) or normalized token.
  proposalGateStatus?: string | null;
  // Set once the quote is converted (Won). The list reads every column.
  salesOrderId?: string | null;
}

// WS-C — proposal is held pending discount approval (gate-before-generate).
// Tolerates the raw column ('PENDING_APPROVAL') + normalized tokens.
function isProposalHeld(p: Proposal): boolean {
  const t = (p.proposalGateStatus ?? '').toString().trim().toLowerCase();
  return t === 'held' || t === 'pending' || t === 'pending_approval';
}

const STATUSES = ['draft', 'sent', 'viewed', 'accepted', 'rejected'];

/** Statuses that count as "the quote has gone out" (server: proposalAcceptableFrom). */
const SENT_LIKE = new Set(['sent', 'viewed', 'negotiation', 'accepted']);

/**
 * 🔒 2026-10-03 (Manak) — a control that shows must work. The row dropdown
 * offers only moves the server accepts:
 *   - Draft: only while still a draft and not converted (409 PROPOSAL_ALREADY_SENT)
 *   - Accepted: only once sent (422 PROPOSAL_NOT_SENT)
 *   - Sent from a held draft: never (423 PROPOSAL_DISCOUNT_HELD)
 * The current status is always kept so the select shows it.
 */
export function proposalStatusOptions(p: Pick<Proposal, 'status' | 'proposalGateStatus' | 'salesOrderId'>): string[] {
  const current = (p.status ?? '').trim().toLowerCase();
  const converted = !!(p.salesOrderId ?? '').trim();
  const isDraft = (current === 'draft' || current === '') && !converted;
  const out = STATUSES.filter((s) => {
    if (s === current) return true;
    if (s === 'draft') return false; // only reached when current !== 'draft'
    if (s === 'accepted') return SENT_LIKE.has(current);
    if (s === 'sent' && isDraft && isProposalHeld(p as Proposal)) return false;
    return true;
  });
  if (current && !out.includes(current)) out.push(current);
  return out;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-50 text-blue-700',
  viewed: 'bg-purple-50 text-purple-700',
  accepted: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-700',
};

export function ProposalsPage() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [creditWarning, setCreditWarning] = useState<
    { warning: CreditWarning; salesOrderId: string | null } | null
  >(null);
  // 2026-10-07 — "Sale marked Won → Mark that follow-up as done?" before the
  // usual after-convert step (credit warning / open the order).
  const [wonFu, setWonFu] = useState<{ due: FollowUpDue; next: () => void } | null>(null);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      params.page = String(page);
      params.limit = String(limit);
      const res = await api.get('/proposals', { params });
      setProposals(res.data.data?.proposals ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load proposals');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, sortField, sortDir, page]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  useEffect(() => {
    setPage(1);
  }, [search, filterStatus]);

  const totalPages = Math.ceil(total / limit);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <div>
      {wonFu && (
        <WonFollowUpPrompt
          due={wonFu.due}
          onDone={() => { const next = wonFu.next; setWonFu(null); next(); }}
        />
      )}
      {creditWarning && (
        <ConvertCreditWarning
          warning={creditWarning.warning}
          onOpenOrder={() =>
            navigate(creditWarning.salesOrderId ? `/sales-orders/${creditWarning.salesOrderId}` : '/sales-orders')
          }
          onDismiss={() => setCreditWarning(null)}
        />
      )}
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proposals</h1>
          <p className="text-sm text-gray-500">Manage your sales proposals</p>
        </div>
        <button
          onClick={() => navigate('/proposals/new')}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Proposal
        </button>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search proposals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          {filterStatus && (
            <button
              onClick={() => setFilterStatus('')}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading proposals...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchProposals} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : proposals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileText size={40} className="mb-2" />
            <p className="text-sm">No proposals found</p>
            <p className="text-xs">Create your first proposal to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('number')}>
                    <span className="flex items-center gap-1">Number <SortIcon field="number" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('title')}>
                    <span className="flex items-center gap-1">Title <SortIcon field="title" /></span>
                  </th>
                  <th className="px-4 py-3">Contact / Account</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('validUntil')}>
                    <span className="flex items-center gap-1">Valid Until <SortIcon field="validUntil" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort('total')}>
                    <span className="flex items-center justify-end gap-1">Total <SortIcon field="total" /></span>
                  </th>
                  <th className="px-4 py-3 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {proposals.map(p => (
                  <tr key={p.id} onClick={() => navigate(`/proposals/${p.id}/edit`)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{p.number}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.title}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {p.contact ? `${joinPersonName(p.contact.firstName, p.contact.lastName)}` : ''}
                      {p.contact && p.account ? ' / ' : ''}
                      {p.account?.name || ''}
                      {!p.contact && !p.account && '-'}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={p.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          try {
                            await api.patch(`/proposals/${p.id}/status`, { status: newStatus });
                            fetchProposals();
                          } catch (err: any) {
                            // 🔒 2026-10-03 — say what the server said (held, unpriced, already sent…).
                            alert(err?.response?.data?.error || 'Failed to update status');
                          }
                        }}
                        className={`inline-flex rounded-full border-0 px-2 py-0.5 text-xs font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)] ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}
                      >
                        {proposalStatusOptions(p).map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                      {/* WS-C — held-pending-discount-approval chip */}
                      {isProposalHeld(p) && (
                        <span
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                          title="Awaiting discount approval before this proposal can be generated"
                        >
                          <Clock size={11} /> Held — awaiting approval
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{p.validUntil ? formatDate(p.validUntil) : '-'}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(p.total)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/api/v1/proposals/${p.id}/pdf`, '_blank');
                          }}
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)] transition-colors"
                          title="Download PDF"
                        >
                          <Download size={15} />
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`Convert ${p.number} to Sales Order?`)) return;
                            try {
                              const res = await api.post(`/proposals/${p.id}/convert-to-sales-order`);
                              const soId = res.data?.data?.id ?? res.data?.id;
                              const after = () => {
                                // 🔒 Dealer credit ledger L6 — the order is made;
                                // the warning is shown, never enforced.
                                const warning = readCreditWarning(res.data);
                                if (warning) { setCreditWarning({ warning, salesOrderId: soId ?? null }); return; }
                                if (soId) navigate(`/sales-orders/${soId}`);
                                else { alert('Sales order created'); navigate('/sales-orders'); }
                              };
                              const due = readFollowUpDue(res.data);
                              if (due) setWonFu({ due, next: after });
                              else after();
                            } catch (err: any) {
                              alert(err.response?.data?.error || 'Failed to convert to Sales Order');
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                          title="Convert to Sales Order"
                        >
                          <ClipboardList size={15} />
                        </button>
                        {p.status === 'accepted' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/invoices/new?fromProposal=${p.id}`);
                            }}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-600 transition-colors"
                            title="Convert to Invoice"
                          >
                            <Receipt size={15} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete proposal ${p.number}?`)) {
                              api.delete(`/proposals/${p.id}`).then(() => fetchProposals()).catch(() => alert('Failed to delete'));
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
